import type { ReactNode } from 'react';

import { NotFound } from '@/components/molecules/NotFound';
import SomethingHappened from '@/components/SomethingHappened';
import { useCage } from '@/contexts/cage';

interface PenGateProps {
    locationId: number;
    children: ReactNode;
}

/**
 * Shows the not-found state for a pen that is not there for this user, or that belongs to
 * another site than the URL names, instead of pages that fail one request at a time.
 *
 * A pen whose context failed for another reason gets the error state with Try again in place
 * of the page. This is the one place that owns that failure for every page under the pen: without the context the pages know neither the pen's name, its licences nor its
 * bioscope, so they rendered an empty title and left out the cards those decide.
 */
export function PenGate(props: PenGateProps) {
    const { locationId, children } = props;
    const { notFound, location, error, retry, isRetrying } = useCage();

    if (notFound || (location !== undefined && location.id !== locationId)) {
        return <NotFound />;
    }
    if (error) {
        return (
            <SomethingHappened
                error={error}
                headingLevel="h1"
                onRetry={retry}
                isRetrying={isRetrying}
            />
        );
    }
    return children;
}
